import { Button, Grid, TextField } from '@mui/material';
import { Box } from '@mui/system';
import axios from 'axios';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import MultipleAndSingleImgValidateWithPreview from 'src/common/FileValidation/MultipleAndSingleImgValidateWithPreview';
import { TOKEN_KEY, url } from 'src/Helpers/utils/Constant';
import Toaster from 'src/Toaster/Toaster';
import CategorySelect from '../ExecutiveCategory/Component/CategorySelect';
import GroupSelect from '../ExecutiveCategory/Component/GroupSelect';

type CreateExecutiveMemberProps = {
  handleClose: () => void;
  setRefreshTrigger: React.Dispatch<React.SetStateAction<number>>;
};

type FormValues = {
  name: string;
  position: string;
  agency_designation: string;
  email: string;
  mobile: string;
  phone: string;
  website: string;
  agency_name: string;
  priority: string | number;
  category_id: string;
  group_id: string;
  address: string;
  files: FileList;
};

export default function CreateExecutiveMember({
  handleClose,
  setRefreshTrigger,
}: CreateExecutiveMemberProps) {
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
    clearErrors,
  } = useForm<FormValues>();

  const [loading, setLoading] = useState<boolean>(false);
  const Toast = Toaster();
  const token = localStorage.getItem(TOKEN_KEY);

  const onSubmit = async (values: FormValues) => {
    try {
      if (!token) {
        Toast.fire({
          icon: 'error',
          title: 'Authentication token is missing. Please log in again.',
        });
        return;
      }

      setLoading(true);

      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('position', values.position);
      formData.append('agency_designation', values.agency_designation);
      formData.append('email', values.email);
      formData.append('mobile', values.mobile);

      if (values.website) {
        formData.append('website', values.website);
      }
      if (values.phone) {
        formData.append('phone', values.phone);
      }
      formData.append('website', values.website);
      formData.append('address', values.address);
      formData.append('agency_name', values.agency_name);
      formData.append('priority', String(values.priority || '0'));
      formData.append('category_id', String(values.category_id));
      formData.append('group_id', String(values.group_id));

      if (values.files && values.files[0]) {
        formData.append('files', values.files[0]);
      }

      const { data } = await axios.post(
        `${url}/api/v1/admin/executive`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (data.success) {
        Toast.fire({
          icon: 'success',
          title: `${data.message}`,
        });
        setRefreshTrigger((prev: number) => prev + 1);
        reset();
        handleClose();
      } else {
        Toast.fire({
          icon: 'error',
          title: `${data.message}`,
        });
      }
    } catch (err: any) {
      Toast.fire({
        icon: 'error',
        title: `${err?.response?.data?.message || 'Something went wrong'}`,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={1} px={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='name'
            label='Enter a name'
            type='text'
            autoFocus
            variant='outlined'
            error={!!errors.name}
            helperText={errors.name?.message}
            {...register('name', { required: 'Name is required' })}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='position'
            label='Enter a designation'
            type='text'
            variant='outlined'
            error={!!errors.position}
            helperText={errors.position?.message}
            {...register('position', { required: 'Designation is required' })}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='email'
            label='Enter an Email'
            type='email'
            variant='outlined'
            error={!!errors.email}
            helperText={errors.email?.message}
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: 'Enter a valid email address',
              },
            })}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='mobile'
            label='Enter a Mobile number'
            type='tel'
            variant='outlined'
            error={!!errors.mobile}
            helperText={errors.mobile?.message}
            {...register('mobile', { required: 'Mobile number is required' })}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            id='phone'
            label='Enter a Phone'
            type='tel'
            variant='outlined'
            error={!!errors.phone}
            helperText={errors.phone?.message}
            {...register('phone')}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            id='address'
            label='Enter a Address'
            type='text'
            variant='outlined'
            required
            {...register('address')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            id='website'
            label='Enter a website'
            type='url'
            variant='outlined'
            error={!!errors.website}
            helperText={errors.website?.message}
            {...register('website')}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='agency_name'
            label='Enter an Agency name'
            type='text'
            variant='outlined'
            error={!!errors.agency_name}
            helperText={errors.agency_name?.message}
            {...register('agency_name', {
              required: 'Agency name is required',
            })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='agency_designation'
            label='Enter an Agency Designation'
            type='text'
            variant='outlined'
            error={!!errors.agency_name}
            helperText={errors.agency_name?.message}
            {...register('agency_designation', {
              required: ' Agency Designation is required',
            })}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            label='Priority (1-5)'
            type='number'
            inputProps={{
              min: 1,
              max: 5,
              step: '0.1',
            }}
            variant='outlined'
            error={!!errors.priority}
            helperText={errors.priority?.message}
            {...register('priority', {
              required: 'Priority is required',
              min: {
                value: 1,
                message: 'Priority must be at least 1',
              },
              max: {
                value: 5,
                message: 'Priority cannot be more than 5',
              },
              validate: (value) =>
                Number(value) >= 1 && Number(value) <= 5
                  ? true
                  : 'Priority must be between 1 and 5',
            })}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          {/* If your CategorySelect supports errors, pass them */}
          <CategorySelect<FormValues> register={register} errors={errors} />
        </Grid>

        <Grid item xs={12} sm={6}>
          <GroupSelect<FormValues> register={register} errors={errors} />
        </Grid>

        <Grid item xs={12} sm={6}>
          <MultipleAndSingleImgValidateWithPreview
            register={register}
            clearErrors={clearErrors}
            setError={setError}
            errors={errors}
            label='Upload image'
            fieldName='files'
            hideCustomLabel
            onlyImage
            mode='single'
            isRequired
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            type='submit'
            fullWidth
            variant='contained'
            disableElevation
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
